/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Set loading to false immediately - don't block UI on background tasks
      setLoading(false);

      // If user just signed in, initialize their free membership (non-blocking)
      if (user) {
        // Check if we've already initialized this user in this session
        const sessionKey = `user_initialized_${user.uid}`;
        const alreadyInitialized = sessionStorage.getItem(sessionKey);

        if (!alreadyInitialized) {
          // Run initialization and domain check in parallel (non-blocking)
          Promise.all([
            // Initialize user
            httpsCallable(functions, 'initialize_new_user')()
              .then(result => {
                if (result.data.success) {
                  console.log('User initialized:', result.data.message);
                  // Mark as initialized in this session
                  sessionStorage.setItem(sessionKey, 'true');
                }
              })
              .catch(error => {
                console.error('Error initializing user membership:', error);
              }),

            // Check domain auto-join
            httpsCallable(functions, 'check_domain_auto_join')()
              .then(domainResult => {
                if (domainResult.data.success && domainResult.data.eligible_teams?.length > 0) {
                  // Store eligible teams in localStorage for the banner to pick up
                  localStorage.setItem('eligibleTeams', JSON.stringify(domainResult.data.eligible_teams));
                  // Trigger a custom event to notify components
                  window.dispatchEvent(new CustomEvent('domainAutoJoinAvailable', {
                    detail: domainResult.data.eligible_teams
                  }));
                }
              })
              .catch(domainError => {
                console.error('Error checking domain auto-join:', domainError);
              })
          ]).catch(error => {
            // Catch any Promise.all errors
            console.error('Error in parallel initialization:', error);
          });
        }
      }
    });

    return unsubscribe;
  }, []);

  const signInWithFirebaseCustomToken = async (customToken) => {
    try {
      // Sign in with Firebase custom token
      const result = await signInWithCustomToken(auth, customToken);
      return result;
    } catch (error) {
      console.error("Custom token sign-in failed:", error);
      throw error;
    }
  };

  const signOutUser = async () => {
    try {
      await signOut(auth);
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.disableAutoSelect();
      }
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value = {
    currentUser,
    loading,
    signInWithFirebaseCustomToken,
    signOutUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 